import { useState } from "react";
import {
  GridLegacy as Grid,
  Paper,
  Box,
  Tooltip,
  IconButton,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { IoMdCar } from "react-icons/io";
import {
  FiArrowRight,
  FiMapPin,
  FiChevronRight,
  FiChevronLeft,
} from "react-icons/fi";
import { RiSparkling2Fill } from "react-icons/ri";
import { format } from "date-fns";

import { IGetBookingsWithPairedJourneyResponse } from "../../../../logic/api";

interface IResultCardProps {
  result: IGetBookingsWithPairedJourneyResponse["results"][0];
}

export default function ResultCard({ result }: IResultCardProps) {
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const { t } = useTranslation();

  const maxSuggestionIndex =
    (result.vehicle.suggestedPairedJourneys?.length || 1) - 1;

  function handleLeftChevronClick() {
    const newIndex =
      suggestionIndex < 1 ? maxSuggestionIndex : suggestionIndex - 1;

    setSuggestionIndex(newIndex);
  }

  function handleRightChevronClick() {
    const newIndex =
      suggestionIndex === maxSuggestionIndex ? 0 : suggestionIndex + 1;

    setSuggestionIndex(newIndex);
  }

  const emptyBy = result.vehicle.suggestedPairedJourneys?.length
    ? result.vehicle.suggestedPairedJourneys[suggestionIndex].emptyBy
    : null;

  return (
    <Grid container columns={4}>
      <Grid
        item
        xs={3}
        component={Paper}
        sx={{ p: 1, display: "flex", alignItems: "center" }}
        square
        variant="outlined"
      >
        <Grid container>
          <Grid item xs>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip title={`Ref: ${result.ref}`}>
                <IconButton>
                  <IoMdCar size={24} />
                </IconButton>
              </Tooltip>
              <Box>
                <Typography fontWeight="bold">
                  {result.vehicle.registration}
                </Typography>
                <Typography fontWeight="bold" variant="caption">
                  {result.vehicle.make} {result.vehicle.model}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip title={t("pair.vehicleTooltip")}>
                <IconButton>
                  <FiMapPin size={24} />
                </IconButton>
              </Tooltip>
              <Box>
                <Typography color="GrayText" variant="caption">
                  {t("pair.currentLocationLabel")}
                </Typography>
                <Typography fontWeight="bold">
                  {result.vehicle.location?.name || t("input.labels.arrivals")}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip title={t("pair.allocatedLocationTooltip")}>
                <IconButton>
                  <FiArrowRight size={24} />
                </IconButton>
              </Tooltip>
              <Box>
                <Typography color="GrayText" variant="caption">
                  {t("pair.allocatedLocationLabel")}
                </Typography>
                <Typography fontWeight="bold">
                  {result.vehicle.allocatedLane?.name ||
                    t("pair.allocatedLocationNull")}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        item
        xs
        component={Paper}
        variant="outlined"
        sx={{
          p: 1,
          ml: 1,
          borderImage: "linear-gradient(135deg, #FFD3A5 10%, #FD6585 100%);",
          borderStyle: "solid",
          borderImageSlice: 1,
          position: "relative",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title={t("pair.suggestionTooltip")}>
            <IconButton>
              <RiSparkling2Fill size={24} color="#FD6585" />
            </IconButton>
          </Tooltip>
          {result.vehicle.suggestedPairedJourneys?.length ? (
            <>
              <Box>
                <Typography fontWeight="bold">
                  {
                    result.vehicle.suggestedPairedJourneys[suggestionIndex]
                      .storageAreaName
                  }
                </Typography>
                <Typography variant="caption">
                  {
                    result.vehicle.suggestedPairedJourneys[suggestionIndex]
                      .totalStoredVehicles
                  }
                  /
                  {
                    result.vehicle.suggestedPairedJourneys[suggestionIndex]
                      .totalCapacity
                  }{" "}
                  spaces utilised
                </Typography>
                <br />
                <Typography variant="caption">
                  {emptyBy
                    ? format(new Date(emptyBy), "do MMM, HH:mm")
                    : "No date available yet"}
                </Typography>
              </Box>
              <Typography
                sx={{ position: "absolute", top: 4, left: 8 }}
                variant="caption"
                color="GrayText"
              >
                {suggestionIndex + 1} / {maxSuggestionIndex + 1}
              </Typography>
              <Box sx={{ position: "absolute", right: 0, top: 0 }}>
                <Tooltip title={t("pair.prevSuggestion")}>
                  <IconButton size="small" onClick={handleLeftChevronClick}>
                    <FiChevronLeft />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("pair.nextSuggestion")}>
                  <IconButton size="small" onClick={handleRightChevronClick}>
                    <FiChevronRight />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          ) : (
            <Box>
              <Typography fontWeight="bold">
                {t("pair.suggestionNull")}
              </Typography>
              <Typography variant="caption">
                {t("pair.suggestionAltNull")}
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
}
